import { MouseEvent, ReactNode, ReactElement, useRef } from 'react';
import styled from 'styled-components';
import { colors } from 'ui/theme/colors';

import { RAW_SELECT_DATA_PORTAL_ID } from 'ui/selects';
import { ActionBar } from './ActionBar';
import { Header } from './Header';
import { DrawerContextProvider } from './Drawer/DrawerContextProvider';
import { Overlay } from './Drawer/Overlay';
import { Drawer, useDrawerContext } from './Drawer';

export const StyledFullScreenLayout = styled.div`
  bottom: 0;
  display: flex;
  flex-direction: column;
  left: 0;
  right: 0;
  overflow: hidden;
  position: fixed;
  top: 0;
`;

export const StyledMain = styled.main`
  flex: 1 1 auto;
  position: relative;
  overflow: hidden;
`;

export const StyledContentContainer = styled.div<{ isAnchored: boolean }>`
  background-color: ${colors.backgroundSecondary};
  display: flex;
  flex-direction: column;
  flex: ${({ isAnchored }) => (isAnchored ? '1 1 auto' : '1 0 auto')};
  height: 100%;
  position: relative;
  ${({ isAnchored }) => (isAnchored ? '' : 'overflow: hidden auto')};
`;

export const StyledContent = styled.div<{ isAnchored: boolean }>`
  display: flex;
  flex-direction: column;
  flex: ${({ isAnchored }) => (isAnchored ? '1 1 auto' : '1 0 auto')};
  ${({ isAnchored }) => (isAnchored ? 'overflow: hidden auto' : '')};
`;

type Props = {
  actionBarIsAnchored?: boolean;
  children: ReactNode;
  DrawerContent: ReactElement;
  FooterContent: ReactElement;
  HeaderContent: ReactElement;
  overlayZIndex?: number;
};

export const FullScreenLayoutInterior = ({
  actionBarIsAnchored = false,
  children,
  DrawerContent,
  FooterContent,
  HeaderContent,
  overlayZIndex,
  ...otherProps // allows StyledFullScreenLayout to be styled with styled-components in parent
}: Props) => {
  const { isDrawerOpen, toggleDrawer } = useDrawerContext();
  const drawerRef = useRef<HTMLDivElement>(null);

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    const selectDropdownPortalEl = document.querySelector(
      `[data-portal-id="${RAW_SELECT_DATA_PORTAL_ID}"]`,
    ); // NOTE: only one of these should exist at a time

    /**
     * If the click originated from the select dropdown(i.e. the dropdown is open),
     * don't close the drawer. The dropdown portal must be close before we start acknowledging
     * clicks outside of the drawer.
     */
    if (selectDropdownPortalEl) {
      return;
    }

    const target = event.target as HTMLDivElement;
    if (isDrawerOpen && drawerRef.current && !drawerRef.current.contains(target)) {
      toggleDrawer();
    }
  };

  return (
    <StyledFullScreenLayout onClick={handleClick} {...otherProps}>
      <Header>{HeaderContent}</Header>
      <StyledMain>
        <Overlay isActive={isDrawerOpen} overlayZIndex={overlayZIndex} />
        <Drawer ref={drawerRef}>{DrawerContent}</Drawer>
        <StyledContentContainer isAnchored={actionBarIsAnchored}>
          <StyledContent isAnchored={actionBarIsAnchored}>{children}</StyledContent>
          <ActionBar>{FooterContent}</ActionBar>
        </StyledContentContainer>
      </StyledMain>
    </StyledFullScreenLayout>
  );
};

export const FullScreenLayout = ({ ...props }: Props) => (
  <DrawerContextProvider>
    <FullScreenLayoutInterior {...props} />
  </DrawerContextProvider>
);
